import { PartnerService, UserService } from "@duck/api-definitions"

export interface CohortPersonDetails {
  personId: string
  email: string
  onboardingStatus: UserService.PersonOnboardingStatusEnum
  assignmentStatus?: PartnerService.AssignmentDetailsStatus
  challengeId?: string
  grade?: number
  feedback?: string
  solution?: string
  createdAt?: string
  startDate?: string
  submitDate?: string
  workTime?: number
  charts?: PartnerService.AssignmentDetails['charts']
  queries?: PartnerService.AssignmentDetails['queries']
}

interface CohortPageData {
  allPersons: Array<CohortPersonDetails>
  taskAssignments: Record<string, CohortPersonDetails[]>
}
  
  export const buildCohortPageData = (
    cohort: PartnerService.Cohort,
    assignmentsDetails: PartnerService.AssignmentDetails[],
    persons: UserService.Person[]
  ): CohortPageData => {
    const personsMap = persons.reduce((acc, person) => {
      acc[person.id] = person
      return acc
    }, {} as Record<string, UserService.Person>)
  
  
    const allPersons: Array<CohortPersonDetails> =
      cohort.personIds
        ?.filter((personId) => personsMap[personId])
        .map((personId) => {
          const person = personsMap[personId]
          // Prefer the assignment with the best status (submitted > started > sent) so we get challengeId when available
          const assignmentDetails = assignmentsDetails
            .filter((ad) => ad.personId === personId)
            .sort((a, b) => {
              const statusOrder: Record<PartnerService.AssignmentDetailsStatus, number> = {
                [PartnerService.AssignmentDetailsStatus.Submitted]: 3,
                [PartnerService.AssignmentDetailsStatus.Started]: 2,
                [PartnerService.AssignmentDetailsStatus.Sent]: 1,
                [PartnerService.AssignmentDetailsStatus.Forfeited]: 0,
              }
              return (statusOrder[b.status] ?? 0) - (statusOrder[a.status] ?? 0)
            })[0]
          return {
            personId,
            email: person.email,
            onboardingStatus: person.onboardingStatus,
            assignmentStatus: assignmentDetails?.status ?? undefined,
            challengeId: assignmentDetails?.challengeId ?? undefined,
            grade: assignmentDetails?.grade ?? undefined,
            feedback: assignmentDetails?.feedback ?? undefined,
            solution: assignmentDetails?.solution ?? undefined,
            createdAt: person.createdAt,
            startDate: assignmentDetails?.startDate ?? undefined,
            submitDate: assignmentDetails?.submitDate ?? undefined,
            workTime: assignmentDetails?.workTime ?? undefined,
            charts: assignmentDetails?.charts ?? undefined,
            queries: assignmentDetails?.queries ?? undefined,
          }
        })
        .sort((a, b) => {
          const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0
          const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0
          return bTime - aTime // Descending order (latest first)
        }) ?? []
  
    const taskAssignments: Record<string, CohortPersonDetails[]> = {}
    assignmentsDetails.forEach(assignmentDetails => {
      const person = personsMap[assignmentDetails.personId]
      if (!person) {
        return
      }
      if (!taskAssignments[assignmentDetails.taskId]) {
        taskAssignments[assignmentDetails.taskId] = []
      }
      taskAssignments[assignmentDetails.taskId].push({
        personId: assignmentDetails.personId,
        email: person.email,
        onboardingStatus: person.onboardingStatus,
        createdAt: person.createdAt,
        assignmentStatus: assignmentDetails.status,
        challengeId: assignmentDetails.challengeId,
        grade: assignmentDetails.grade,
        feedback: assignmentDetails.feedback,
        solution: assignmentDetails.solution,
        startDate: assignmentDetails.startDate,
        submitDate: assignmentDetails.submitDate,
        workTime: assignmentDetails.workTime,
        charts: assignmentDetails.charts,
        queries: assignmentDetails.queries,
      })
    })
  
    return { allPersons, taskAssignments }
  }